import { useEffect, useMemo, useRef } from "react";
import "./Calendar.css";
import CalendarItem from "./CalendarItem";

function formatDateKey(date) {
  return date.toLocaleDateString("sv-SE");
}

function isWeekend(date) {
  const day = date.getDay();
  return day === 0 || day === 6;
}

export default function MobileMonthlyList({
  date = new Date(),
  showWeekends = true,
  onSelectDay,
  data = [],
}) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const today = new Date();
  const listRef = useRef(null);
  const todayCardRef = useRef(null);
  const lastAutoScrollKeyRef = useRef("");

  const dataByDate = useMemo(() => {
    const map = new Map();

    if (!Array.isArray(data)) return map;

    data.forEach((entry) => {
      const key = formatDateKey(new Date(entry.scheduleDate));
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(entry);
    });

    for (const [key, entries] of map) {
      entries.sort((a, b) => {
        const [aH, aM] = a.startTime ? a.startTime.split(":").map(Number) : [0, 0];
        const [bH, bM] = b.startTime ? b.startTime.split(":").map(Number) : [0, 0];
        return aH * 60 + aM - (bH * 60 + bM);
      });
    }

    return map;
  }, [data]);

  const monthDays = useMemo(() => {
    const result = [];
    const totalDays = new Date(year, month + 1, 0).getDate();

    for (let day = 1; day <= totalDays; day++) {
      const itemDate = new Date(year, month, day);
      if (!showWeekends && isWeekend(itemDate)) continue;

      const key = formatDateKey(itemDate);
      const entries = dataByDate.get(key) || [];
      result.push({ date: itemDate, entries });
    }

    return result;
  }, [year, month, showWeekends, dataByDate]);

  useEffect(() => {
    const monthKey = `${year}-${month}`;
    if (lastAutoScrollKeyRef.current === monthKey) return;

    const currentDate = new Date();
    const isCurrentMonth =
      currentDate.getFullYear() === year && currentDate.getMonth() === month;

    if (isCurrentMonth && todayCardRef.current) {
      todayCardRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    } else if (listRef.current) {
      listRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }

    lastAutoScrollKeyRef.current = monthKey;
  }, [year, month]);

  return (
    <div className="mobile-monthly-list" ref={listRef}>
      {monthDays.map(({ date: itemDate, entries }) => {
        const isToday =
          itemDate.getDate() === today.getDate() &&
          itemDate.getMonth() === today.getMonth() &&
          itemDate.getFullYear() === today.getFullYear();

        const weekday = itemDate.toLocaleDateString("default", {
          weekday: "short",
        });
        const dateLabel = itemDate.toLocaleDateString("default", {
          day: "numeric",
          month: "short",
          year: "numeric",
        });

        return (
          <button
            key={formatDateKey(itemDate)}
            type="button"
            className={`mobile-monthly-day-card ${isToday ? "today" : ""}`}
            onClick={() => onSelectDay?.(itemDate)}
            ref={isToday ? todayCardRef : null}
          >
            <div className="mobile-monthly-day-card-header">
              <div className="mobile-monthly-day-meta">
                <span className="mobile-monthly-day-weekday">{weekday}</span>
                <span className="mobile-monthly-day-date">{dateLabel}</span>
              </div>
              <span className="mobile-monthly-day-count">
                {entries.length} trip{entries.length === 1 ? "" : "s"}
              </span>
            </div>

            {entries.length > 0 ? (
              <div className="mobile-monthly-day-items">
                {entries.slice(0, 2).map((entry, index) => (
                  <CalendarItem
                    key={entry._id || `${formatDateKey(itemDate)}-${index}`}
                    data={entry}
                    compact={true}
                    remainingCount={
                      index === 1 && entries.length > 2 ? entries.length - 2 : 0
                    }
                  />
                ))}
              </div>
            ) : (
              <div className="mobile-monthly-day-empty">No schedules</div>
            )}
          </button>
        );
      })}
    </div>
  );
}
